import activeEditor from 'domains/active_editor';
import catalogs from 'domains/catalogs';
import products from 'domains/products';
import subcatalogs from 'domains/subcatalogs';
import canvases from 'domains/canvases';

import { EDIT_CART_PRODUCT_PARAM, ROUTE_MAP } from 'utils/globals';
import { buildConfiguredURL, updateDocumentTitle } from 'utils/routing';

import selectors from './navigation.selectors';
import slice from './navigation.slice';

import type { AppThunk } from 'store/wos_store';
import type { NavigationRoute } from './navigation.types';

function navigateTo(
  nextRoute: NavigationRoute | null,
  popStateTarget?: Window,
  shouldReplace?: boolean
): AppThunk {
  return function navigateThunk(dispatch, getState) {
    const state = getState();
    const currentRoute = selectors.current(state);
    const wasEditorPopstate = selectors.wasEditorPopstate(state);
    const configuredPathHistory = selectors.history(state);
    const canvasIds = canvases.selectors.selectIds(state);

    const editCartItem = activeEditor.selectors.cartProductId(state);
    const isCartProduct = !!editCartItem;

    // When popstate occurs, we reconfigure the previous product at the bottom of this thunk
    // This triggers a configPath change, but we don't want to duplicate the route setting
    // So we break out early, but reset the redux state so future configs changes can continue
    if (wasEditorPopstate) {
      dispatch(slice.actions.setWasEditorPopstate(false));
      return;
    }

    const configPath = selectors.canvasConfigPath(state);
    const configParams = selectors.optionsQueryParam(state);
    const customSizeParam = selectors.customSizeQueryParam(state);

    const isPopState = !!popStateTarget;

    // If no next route, we're dealing with popstate from initial load so use that route
    const toRoute = nextRoute || state.navigation.initialRoute;

    const routeChange = `${currentRoute}-${toRoute}`;

    // Default to arg value, will also be set true on editor<->cart navigation when editing cart item
    // Or, use replace if we're configuring the first product from a non-fully-configured path
    let shouldUseReplaceState = shouldReplace || configuredPathHistory.length === 0;
    // Placeholder for history state update url
    let url;

    if (!isPopState) {
      switch (routeChange) {
        case 'editor-cart':
          url = ROUTE_MAP[toRoute];
          shouldUseReplaceState = isCartProduct ? true : shouldUseReplaceState;
          break;
        case 'catalogSelector-editor': // Not used since catalog selector uses hard links, but could be added here for no-reload selection
        case 'priceTool-editor':
          url = buildConfiguredURL(
            new URL(window.location.href),
            ROUTE_MAP.editor,
            configPath,
            configParams,
            customSizeParam
          );
          break;
        case 'cart-editor': {
          if (isCartProduct) {
            url = new URL(window.location.href);
            const urlParams = url.searchParams || new URLSearchParams();
            urlParams.set(EDIT_CART_PRODUCT_PARAM, editCartItem.toString());
            url.search = urlParams.toString();
            shouldUseReplaceState = true;
          } else {
            url = `${ROUTE_MAP.editor}${configPath}`;
          }
          break;
        }
        case 'priceTool-priceTool':
          shouldUseReplaceState = true;
        // intentional fallthrough
        case 'editor-editor': {
          url = new URL(window.location.href);
          url = buildConfiguredURL(
            url,
            ROUTE_MAP[toRoute],
            configPath,
            configParams,
            customSizeParam
          );

          if (canvasIds.length > 1) return;
          dispatch(slice.actions.addConfiguredPathHistory(configPath));
          break;
        }
        default:
          url = ROUTE_MAP[toRoute];
          break;
      }
    }

    // Reconfigure the previous product
    if (isPopState && routeChange === 'editor-editor') {
      const catalogIdsBySlug = catalogs.selectors.idsBySlug(state);
      const subcatalogIdsBySlug = subcatalogs.selectors.idsBySlug(state);
      const productIDsBySlug = products.selectors.bySlug(state);

      // first will be empty string, second will be "c"
      const [, , catalogSlug, subcatalogSlug, productSlug] =
        popStateTarget.location.pathname.split('/');

      const prevConfig = {
        catalog: catalogIdsBySlug[catalogSlug],
        subcatalog: subcatalogIdsBySlug[subcatalogSlug],
        product: productIDsBySlug[productSlug],
      };

      // Sets the flag that indicates the next config change should not update the route
      dispatch(slice.actions.setWasEditorPopstate(true));
      dispatch(canvases.actions.changeActiveCanvasConfig.multi(prevConfig));
    }

    // We don't want to touch the history if this navigation was triggered by a popstate event
    if (!isPopState) {
      if (shouldUseReplaceState) {
        window.history.replaceState({ route: nextRoute }, '', url);
      } else {
        window.history.pushState({ route: nextRoute }, '', url);
      }
    }

    // Update the title of the document
    updateDocumentTitle(toRoute);

    // Always udpate the current route in state when we're done with all other configurations
    dispatch(slice.actions.updateCurrentRoute(toRoute));
  };
}

export default {
  navigateTo,
};
